// =============================================================================
// [ファイル] AssistantMailAutomationTab.tsx
// [概要]   エージェント設定ダイアログの「自動処理」タブ。
//          - 監視の状態カード (受信トレイ・稼働中/期限切れ/未登録・期限・最終通知・今日の件数・再登録)
//          - ルール一覧 (有効/無効の切替、編集、削除、追加)
//          - ルール編集ダイアログ (条件 → 保存先 → 参照する定義 → 動作) と、保存前のテスト投入
//            参照する定義: ルール・スキル (個人の定義。混在で複数、ドラッグで優先順。有効化には 1 件以上)、
//                          取組み課題 (個人・共有を問わず任意)
//            (条件に一致する直近メールを探し、本番と同じ経路で AI に流す。送信はしない)
//          - 処理履歴 (通知ごとの判定。会話を作成した行から会話を開ける)
// =============================================================================
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import * as Icons from '../constants/icons';
import LoadingView from './LoadingView';
import { STANDARD_CTA_BUTTON_SX, STANDARD_TEXT_BUTTON_SX } from '../constants/buttonStyles';
import { useNotification } from '../providers/NotificationProvider';
import { useConfirm } from '../providers/ConfirmProvider';
import type { AgentBuckets, AgentGroup, AgentItem } from '../hooks/useAssistantAgents';
import {
  useAssistantMailAutomation,
  MAIL_RULE_ACTION_LABELS,
  type MailAutomationRecord,
  type MailAutomationRule,
  type MailAutomationWatch,
  type MailRuleAction,
  type MailRuleInput,
  type MailTestCandidate,
} from '../hooks/useAssistantMailAutomation';

interface Props {
  active: boolean; // タブが表示されている間だけ取得する
  groups: AgentGroup[]; // 保存先グループの候補 (有効なもの)
  allGroups: AgentGroup[]; // 定義の「個人」判定に使う全グループ (無効なものも含む)
  agents: AgentBuckets; // 参照先の候補 (スキル・ルール・取組み課題)
  currentUserEmail: string;
  onOpenSession: (sessionId: string) => void; // 処理履歴から会話を開く (ダイアログは閉じる)
}

const NEW_RULE: MailRuleInput = {
  name: '',
  enabled: true,
  from_email: '',
  subject_contains: '',
  group_id: '',
  title_format: '{date} {name}',
  def_ids: [],
  issue_ids: [],
  note: '',
  action: 'draft_reply',
  fixed_cc: '',
  fixed_bcc: '',
};

const DEF_TYPE_LABEL: Record<string, string> = { rule: 'ルール', skill: 'スキル' };

/** 選んだルール・スキルの優先順リストの 1 行 (ドラッグで並べ替え) */
function SortableDefRow({ item, index, onRemove }: { item: AgentItem; index: number; onRemove: () => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  return (
    <Box
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        px: 0.75,
        py: 0.25,
        bgcolor: '#fff',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        opacity: isDragging ? 0.6 : 1,
        position: 'relative',
        zIndex: isDragging ? 1 : 'auto',
      }}
    >
      <Box {...attributes} {...listeners} sx={{ display: 'flex', cursor: 'grab', color: blueGrey[300] }}>
        <Icons.DragIndicator sx={{ fontSize: 18 }} />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ width: 16 }}>
        {index + 1}
      </Typography>
      <Chip
        label={DEF_TYPE_LABEL[item.type] ?? item.type}
        size="small"
        color={item.type === 'skill' ? 'primary' : 'secondary'}
        variant="outlined"
        sx={{ height: 18, fontSize: '0.62rem' }}
      />
      <Typography sx={{ fontSize: '0.8rem', flexGrow: 1 }}>{item.name}</Typography>
      <IconButton size="small" onClick={onRemove}>
        <Icons.Close sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
}

/** 本人だけが編集できる定義か (グループ付きは本人の個人グループ、共通は personal かつ本人作成)。
 *  バックエンドの判定 (resolve_refs の _is_personal) と同じ基準 */
function isPersonalDef(a: AgentItem, me: string, groupById: Map<string, AgentGroup>): boolean {
  if (a.group_id) {
    const g = groupById.get(a.group_id);
    return Boolean(g) && g!.user_id === me && g!.scope === 'personal';
  }
  return a.scope === 'personal' && (a.user_id ?? me) === me;
}

const ISSUE_STATUS_LABEL: Record<string, string> = { open: '未着手', in_progress: '対応中', done: '完了' };

/** 取組み課題の表示用の簡易判定 (共有メンバーがいる・他人作成なら共有扱い) */
function isPersonalDefLoose(a: AgentItem, me: string): boolean {
  return (a.user_id ?? me) === me && a.scope === 'personal';
}

/** ISO 日時を「MM/DD HH:mm」に (JST はブラウザのローカル時刻) */
function fmt(iso: string | null | undefined, withYear = false): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  const p = (n: number) => String(n).padStart(2, '0');
  const md = `${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
  return withYear ? `${d.getFullYear()}/${md}` : md;
}

/** 差出人の表示名だけ (「"山下佳織" <x@y>」→ 山下佳織) */
function senderName(from: string): string {
  const m = from.match(/^"?([^"<]+?)"?\s*<[^>]+>$/);
  return (m ? m[1] : from).trim();
}

const RECORD_STATUS: Record<string, { label: string; color: 'success' | 'default' | 'error' | 'info' }> = {
  queued: { label: '会話を作成', color: 'success' },
  skipped: { label: '対象外', color: 'default' },
  error: { label: 'エラー', color: 'error' },
  processing: { label: '処理中', color: 'info' },
};

/** 監視状態の 3 値 (稼働中 / 期限切れ / 未登録) と注意 */
function watchView(w: MailAutomationWatch | undefined) {
  if (!w || !w.registered) {
    return { label: '未登録', color: blueGrey[400], icon: <Icons.Block sx={{ fontSize: 18 }} />, warn: '監視が登録されていません。有効なルールを保存するか「監視を再登録」で登録します。' };
  }
  if (w.expired) {
    return { label: '期限切れ', color: '#d32f2f', icon: <Icons.WarningAmber sx={{ fontSize: 18 }} />, warn: '監視の期限が切れています。通知が止まっているため「監視を再登録」してください。' };
  }
  if (w.stale) {
    return { label: '稼働中 (通知なし)', color: '#ed6c02', icon: <Icons.WarningAmber sx={{ fontSize: 18 }} />, warn: '最終通知から 1 日以上経っています。受信トレイに新着があるのに通知が無ければ配管を疑ってください。' };
  }
  return { label: '稼働中', color: '#2e7d32', icon: <Icons.CheckCircle sx={{ fontSize: 18 }} />, warn: '' };
}

function toInput(r: MailAutomationRule): MailRuleInput {
  return {
    id: r.id,
    name: r.name,
    enabled: r.enabled,
    from_email: r.from_email,
    subject_contains: r.subject_contains,
    group_id: r.group_id,
    title_format: r.title_format,
    def_ids: r.def_ids ?? [],
    issue_ids: r.issue_ids ?? [],
    note: r.note ?? '',
    action: r.action,
    fixed_cc: r.fixed_cc ?? '',
    fixed_bcc: r.fixed_bcc ?? '',
  };
}

// ---------------------------------------------------------------- ルール編集ダイアログ
interface EditProps {
  open: boolean;
  initial: MailRuleInput;
  groups: AgentGroup[];
  defs: AgentItem[]; // 選べるルール・スキル (個人のみ)
  issues: AgentItem[]; // 選べる取組み課題 (見えるものすべて)
  groupName: (groupId?: string) => string;
  me: string;
  onClose: () => void;
  onSave: (input: MailRuleInput) => Promise<void>;
  testCandidates: (input: MailRuleInput) => Promise<MailTestCandidate[]>;
  testRun: (input: MailRuleInput, messageId: string) => Promise<string>;
  onTested: () => void; // テスト投入後に処理履歴を取り直す
}

function RuleEditDialog({
  open,
  initial,
  groups,
  defs,
  issues,
  groupName,
  me,
  onClose,
  onSave,
  testCandidates,
  testRun,
  onTested,
}: EditProps) {
  const notify = useNotification();
  const confirm = useConfirm();
  const [form, setForm] = useState<MailRuleInput>(initial);
  const [saving, setSaving] = useState(false);
  const [searching, setSearching] = useState(false);
  const [running, setRunning] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<MailTestCandidate[] | null>(null);

  useEffect(() => {
    if (open) {
      setForm(initial);
      setCandidates(null);
    }
  }, [open, initial]);

  const set = <K extends keyof MailRuleInput>(key: K, value: MailRuleInput[K]) => setForm((f) => ({ ...f, [key]: value }));
  const busy = saving || searching || Boolean(running);
  const defById = new Map(defs.map((d) => [d.id, d]));
  const selectedDefs = form.def_ids.map((id) => defById.get(id)).filter((d): d is AgentItem => Boolean(d));
  const missingDefCount = form.def_ids.length - selectedDefs.length;
  const noDefs = selectedDefs.length === 0;
  const dragSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const handleDefDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const ids = selectedDefs.map((d) => d.id);
    set('def_ids', arrayMove(ids, ids.indexOf(String(active.id)), ids.indexOf(String(over.id))));
  };

  const cleaned = (): MailRuleInput => ({ ...form, def_ids: selectedDefs.map((d) => d.id) });
  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(cleaned());
      onClose();
    } catch (e) {
      notify.error(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleSearch = async () => {
    setSearching(true);
    try {
      const list = await testCandidates(form);
      setCandidates(list);
      if (list.length === 0) notify.info('条件に一致するメールは直近 30 日にありません');
    } catch (e) {
      notify.error(e instanceof Error ? e.message : '検索に失敗しました');
    } finally {
      setSearching(false);
    }
  };

  const handleRun = async (c: MailTestCandidate) => {
    const ok = await confirm({
      title: 'テスト投入',
      message:
        `「${c.subject || '(件名なし)'}」をこのルールの内容で AI に流します。` +
        (form.action === 'draft_reply'
          ? ' 会話が作られ、Gmail のスレッドに返信の下書きが入ります (送信はしません)。'
          : ' 会話が作られ、要約と所見が残ります (下書きも送信もしません)。') +
        ' よろしいですか？',
      confirmText: '投入する',
    });
    if (!ok) return;
    setRunning(c.message_id);
    try {
      await testRun(cleaned(), c.message_id);
      notify.success('テスト投入しました。AI の回答は処理履歴の「開く」から確認できます');
      onTested();
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'テスト投入に失敗しました');
    } finally {
      setRunning(null);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth disableScrollLock>
      <DialogTitle sx={{ fontSize: '0.875rem', fontWeight: 600, py: 0.75, px: 2, display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Icons.Bolt sx={{ color: 'warning.main', fontSize: 18 }} />
        {form.id ? 'ルールを編集' : 'ルールを追加'}
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: '#fafafa' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <TextField
            label="ルール名"
            size="small"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="例: 山下さんの日報"
            inputProps={{ maxLength: 60 }}
          />
          <Typography variant="caption" sx={{ fontWeight: 700, color: blueGrey[700], mt: 0.5 }}>1. 条件 (本人の受信トレイに届いたメール)</Typography>
          <TextField
            label="差出人のメールアドレス"
            size="small"
            value={form.from_email}
            onChange={(e) => set('from_email', e.target.value)}
            placeholder="例: kaori_yamashita@hozenall.co.jp"
          />
          <TextField
            label="件名に含む語 (任意)"
            size="small"
            value={form.subject_contains}
            onChange={(e) => set('subject_contains', e.target.value)}
            placeholder="例: 日報をお送りします"
            inputProps={{ maxLength: 200 }}
          />

          <Typography variant="caption" sx={{ fontWeight: 700, color: blueGrey[700], mt: 0.5 }}>2. 保存先 (会話を作る場所)</Typography>
          <FormControl size="small">
            <InputLabel id="mail-rule-group">保存先グループ</InputLabel>
            <Select
              labelId="mail-rule-group"
              label="保存先グループ"
              value={form.group_id}
              onChange={(e) => set('group_id', String(e.target.value))}
              MenuProps={{ disableScrollLock: true }}
            >
              <MenuItem value="">共通 (グループなし)</MenuItem>
              {groups.map((g) => (
                <MenuItem key={g.id} value={g.id}>
                  {g.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="会話のタイトル"
            size="small"
            value={form.title_format}
            onChange={(e) => set('title_format', e.target.value)}
            helperText="{date} = 件名か受信日の日付、{name} = ルール名、{subject} = 件名、{sender} = 差出人"
          />

          <Typography variant="caption" sx={{ fontWeight: 700, color: blueGrey[700], mt: 0.5 }}>
            3. 参照する定義 (AI にはここで選んだものだけを渡します。共通・グループの定義は渡しません)
          </Typography>
          <Autocomplete
            multiple
            size="small"
            options={defs}
            groupBy={(o) => DEF_TYPE_LABEL[o.type] ?? o.type}
            value={selectedDefs}
            onChange={(_, v) => set('def_ids', v.map((d) => d.id))}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderValue={() => null}
            renderOption={(props, o) => (
              <li {...props} key={o.id}>
                {o.name}
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  {groupName(o.group_id)}
                </Typography>
              </li>
            )}
            renderInput={(p) => (
              <TextField
                {...p}
                label="適用するルール・スキル (個人の定義から複数選択)"
                error={form.enabled && noDefs}
                helperText={
                  form.enabled && noDefs
                    ? '有効にするには 1 件以上選んでください'
                    : '選んだものは下の順に AI へ渡します。先に並ぶものを優先します (ドラッグで並べ替え)'
                }
              />
            )}
          />
          {missingDefCount > 0 && (
            <Alert severity="error" sx={{ fontSize: '0.76rem', py: 0 }}>
              選択中の {missingDefCount} 件が個人の定義として見つかりません (削除・共有化・無効化された可能性)。保存すると外れます。
            </Alert>
          )}
          {selectedDefs.length > 0 && (
            <DndContext sensors={dragSensors} collisionDetection={closestCenter} onDragEnd={handleDefDragEnd}>
              <SortableContext items={selectedDefs.map((d) => d.id)} strategy={verticalListSortingStrategy}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {selectedDefs.map((d, i) => (
                    <SortableDefRow
                      key={d.id}
                      item={d}
                      index={i}
                      onRemove={() => set('def_ids', form.def_ids.filter((x) => x !== d.id))}
                    />
                  ))}
                </Box>
              </SortableContext>
            </DndContext>
          )}
          <Autocomplete
            multiple
            size="small"
            options={issues}
            value={issues.filter((i) => form.issue_ids.includes(i.id))}
            onChange={(_, v) => set('issue_ids', v.map((i) => i.id))}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderOption={(props, o) => {
              const shared = !isPersonalDefLoose(o, me);
              return (
                <li {...props} key={o.id}>
                  {o.name}
                  <Chip
                    label={`${shared ? '共有' : '個人'}・${ISSUE_STATUS_LABEL[o.status ?? 'open'] ?? o.status}`}
                    size="small"
                    variant="outlined"
                    sx={{ height: 18, fontSize: '0.62rem', ml: 1 }}
                  />
                </li>
              );
            }}
            renderInput={(p) => <TextField {...p} label="参照する取組み課題 (任意・背景知識として渡す)" />}
          />
          <TextField
            label="補足の指示 (任意。このルールだけの一言)"
            size="small"
            multiline
            minRows={2}
            maxRows={6}
            value={form.note}
            onChange={(e) => set('note', e.target.value)}
            inputProps={{ maxLength: 2000 }}
            helperText="書き方の本体はスキル側で育ててください。ここはこのメールに固有の一言だけにします"
            sx={{ '& textarea': { fontSize: '0.8rem' } }}
          />

          <Typography variant="caption" sx={{ fontWeight: 700, color: blueGrey[700], mt: 0.5 }}>4. 動作 (送信を伴う動作は選べません)</Typography>
          <RadioGroup value={form.action} onChange={(e) => set('action', e.target.value as MailRuleAction)}>
            {(Object.keys(MAIL_RULE_ACTION_LABELS) as MailRuleAction[]).map((a) => (
              <FormControlLabel
                key={a}
                value={a}
                control={<Radio size="small" />}
                label={
                  <Typography sx={{ fontSize: '0.8rem' }}>
                    {MAIL_RULE_ACTION_LABELS[a]}
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      {a === 'draft_reply' ? 'スレッド内に返信の下書きを入れ、Chat で知らせる' : '会話に要約と所見を残し、Chat で知らせる'}
                    </Typography>
                  </Typography>
                }
              />
            ))}
          </RadioGroup>
          {form.action === 'draft_reply' && (
            <Box sx={{ pl: 3.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                返信下書きに毎回付ける宛先 (任意)。ここに登録した宛先だけが付き、AI やスキルの記述では宛先を変えられません。複数はカンマ区切り (各 5 件まで)
              </Typography>
              <TextField
                label="Cc (固定)"
                size="small"
                value={form.fixed_cc}
                onChange={(e) => set('fixed_cc', e.target.value)}
                placeholder="例 someone@hozenall.co.jp"
                inputProps={{ style: { fontSize: '0.8rem' } }}
              />
              <TextField
                label="Bcc (固定)"
                size="small"
                value={form.fixed_bcc}
                onChange={(e) => set('fixed_bcc', e.target.value)}
                placeholder="例 taro_fukuoka@hozenall.co.jp"
                inputProps={{ style: { fontSize: '0.8rem' } }}
              />
            </Box>
          )}
          <FormControlLabel
            control={<Switch size="small" checked={form.enabled} onChange={(e) => set('enabled', e.target.checked)} />}
            label={
              <Typography sx={{ fontSize: '0.8rem' }}>
                有効 (ルール・スキルを 1 件以上選ぶと有効にできます。保存すると受信トレイの監視が自動で登録されます)
              </Typography>
            }
          />

          {/* テスト投入 */}
          <Box sx={{ mt: 1, p: 1.25, borderRadius: 1, bgcolor: '#fff', border: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, flexGrow: 1 }}>テスト投入 (保存前に本番と同じ処理で確かめる)</Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Icons.Search />}
                onClick={() => void handleSearch()}
                disabled={busy || !form.from_email.includes('@')}
                sx={{ fontSize: '0.72rem', py: 0.25, textTransform: 'none' }}
              >
                一致するメールを探す (直近 30 日)
              </Button>
            </Box>
            {searching && <LoadingView message="Gmail を検索しています..." height="50px" size={22} />}
            {candidates && candidates.length > 0 && (
              <Table size="small" sx={{ '& td, & th': { fontSize: '0.74rem', py: 0.4 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>受信日</TableCell>
                    <TableCell>件名</TableCell>
                    <TableCell>処理</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {candidates.map((c) => (
                    <TableRow key={c.message_id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmt(c.date)}</TableCell>
                      <TableCell sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Tooltip title={c.snippet}>
                          <span>{c.subject || '(件名なし)'}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {c.processed_status ? (
                          <Chip label={RECORD_STATUS[c.processed_status]?.label ?? c.processed_status} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                        ) : (
                          <Typography variant="caption" color="text.secondary">未処理</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Button
                          size="small"
                          onClick={() => void handleRun(c)}
                          disabled={busy || noDefs}
                          startIcon={<Icons.PlayArrow sx={{ fontSize: 14 }} />}
                          sx={{ fontSize: '0.72rem', py: 0, textTransform: 'none' }}
                        >
                          {running === c.message_id ? '投入中...' : 'このメールで試す'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {candidates && candidates.length === 0 && (
              <Typography variant="caption" color="text.secondary">条件に一致するメールは直近 30 日にありません。</Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 0.75 }}>
        <Button size="small" onClick={onClose} disabled={busy} sx={{ ...STANDARD_TEXT_BUTTON_SX, fontSize: '0.78rem', py: 0.25 }}>
          キャンセル
        </Button>
        <Button
          size="small"
          onClick={() => void handleSave()}
          disabled={busy || (form.enabled && noDefs)}
          startIcon={<Icons.Save />}
          sx={{ ...STANDARD_CTA_BUTTON_SX, fontSize: '0.78rem', py: 0.25 }}
        >
          {saving ? '保存中...' : '保存する'}
        </Button>
      </DialogActions>
      {/* 保存中・テスト投入中は画面全体に重ねて出す (保存ボタンは下端にあり、上端のローディングはスクロールで見えないため)。
          監視の自動登録を伴う保存は数秒かかることがある */}
      {(saving || Boolean(running)) && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            bgcolor: 'rgba(255, 255, 255, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <LoadingView
            message={saving ? 'ルールを保存しています...' : 'テスト投入しています...'}
            height="auto"
            size={40}
          />
        </Box>
      )}
    </Dialog>
  );
}

// ---------------------------------------------------------------- タブ本体
export default function AssistantMailAutomationTab({ active, groups, allGroups, agents, currentUserEmail, onOpenSession }: Props) {
  const notify = useNotification();
  const confirm = useConfirm();
  const { status, loading, registering, error, refresh, register, saveRule, deleteRule, testCandidates, testRun } =
    useAssistantMailAutomation(active);
  const [showSkipped, setShowSkipped] = useState(false);
  const [editing, setEditing] = useState<MailRuleInput | null>(null);
  const [busyRuleId, setBusyRuleId] = useState<string | null>(null);

  const view = watchView(status?.watch);
  const records = useMemo(() => {
    const all = status?.records ?? [];
    return showSkipped ? all : all.filter((r) => r.status !== 'skipped');
  }, [status?.records, showSkipped]);
  const skippedCount = (status?.records ?? []).filter((r) => r.status === 'skipped').length;
  const ruleNameById = useMemo(() => new Map((status?.rules ?? []).map((r) => [r.id, r.name])), [status?.rules]);

  // 参照先の候補。スキル・ルールは個人の定義のみ (他人の編集で自動処理の動きが変わらないように)、
  // 取組み課題は背景知識なので見えるものすべて (完了も含め、選んだものは渡す)
  const groupById = useMemo(() => new Map(allGroups.map((g) => [g.id, g])), [allGroups]);
  const groupName = (gid?: string) => (gid ? `グループ「${groupById.get(gid)?.name ?? '?'}」` : '共通');
  // 選べるルール・スキル (個人の定義のみ)。ルール → スキルの順に並べる (選択欄で種類ごとに見出しを付ける)
  const personalDefs = useMemo(
    () =>
      [...agents.rules, ...agents.skills].filter(
        (a) => a.enabled !== false && isPersonalDef(a, currentUserEmail, groupById)
      ),
    [agents.rules, agents.skills, currentUserEmail, groupById]
  );
  const visibleIssues = agents.issues;

  const handleRegister = async () => {
    try {
      await register();
      notify.success('監視を登録し直しました');
    } catch (e) {
      notify.error(e instanceof Error ? e.message : '再登録に失敗しました');
    }
  };

  const handleSave = async (input: MailRuleInput) => {
    const res = await saveRule(input);
    notify.success(res.watch_registered ? 'ルールを保存し、受信トレイの監視を登録しました' : 'ルールを保存しました');
    void refresh();
  };

  const handleToggle = async (r: MailAutomationRule, enabled: boolean) => {
    if (enabled && (r.def_ids ?? []).length === 0) {
      notify.warning('有効にするには、編集画面でルールかスキルを 1 件以上選んでください');
      return;
    }
    setBusyRuleId(r.id);
    try {
      await saveRule({ ...toInput(r), enabled });
      notify.success(enabled ? `「${r.name}」を有効にしました` : `「${r.name}」を無効にしました`);
      void refresh();
    } catch (e) {
      notify.error(e instanceof Error ? e.message : '更新に失敗しました');
    } finally {
      setBusyRuleId(null);
    }
  };

  const handleDelete = async (r: MailAutomationRule) => {
    const ok = await confirm({
      title: 'ルールの削除',
      message: `「${r.name}」を削除しますか？ これまでに作られた会話と処理履歴は残ります。`,
      confirmText: '削除する',
      destructive: true,
    });
    if (!ok) return;
    setBusyRuleId(r.id);
    try {
      await deleteRule(r.id);
      notify.success('ルールを削除しました');
    } catch (e) {
      notify.error(e instanceof Error ? e.message : '削除に失敗しました');
    } finally {
      setBusyRuleId(null);
    }
  };

  if (loading && !status) {
    return <LoadingView message="自動処理の状態を読み込んでいます..." height="40vh" size={44} />;
  }

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2, fontSize: '0.8rem' }}>
        受信トレイに届いたメールをルール (差出人・件名) で判定し、一致したものは AI が会話を作って返信の下書きや要約を用意します。
        書き方は、ルールで選んだ個人のルール・スキル (と任意の取組み課題) に従います。送信は行いません。
        ルールは保存前にテスト投入で確かめられます。
      </Alert>
      {error && (
        <Alert severity="error" sx={{ mb: 2, fontSize: '0.8rem' }}>
          {error}
        </Alert>
      )}

      {/* 監視の状態カード */}
      <Box sx={{ p: 1.5, mb: 2, borderRadius: 1, bgcolor: '#fff', border: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Icons.Bolt sx={{ color: 'warning.main', fontSize: 20 }} />
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, flexGrow: 1 }}>メール自動処理の監視</Typography>
          <Tooltip title="最新の状態を取り直す">
            <span>
              <IconButton size="small" onClick={() => void refresh()} disabled={loading || registering}>
                <Icons.Refresh sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            size="small"
            startIcon={<Icons.RestartAlt />}
            onClick={() => void handleRegister()}
            disabled={registering || loading || !status}
            sx={{ ...STANDARD_CTA_BUTTON_SX, fontSize: '0.75rem', py: 0.25 }}
          >
            監視を再登録
          </Button>
        </Box>
        {registering && <LoadingView message="監視を登録し直しています..." height="60px" size={24} />}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '120px 1fr' }, rowGap: 0.5, columnGap: 1, fontSize: '0.8rem' }}>
          <Typography variant="caption" color="text.secondary">受信トレイ</Typography>
          <Typography sx={{ fontSize: '0.8rem' }}>{status?.mailbox ?? '-'}</Typography>

          <Typography variant="caption" color="text.secondary">状態</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: view.color, flexWrap: 'wrap' }}>
            {view.icon}
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'inherit' }}>{view.label}</Typography>
            {status?.watch.registered && (
              <Typography variant="caption" color="text.secondary">
                (期限 {fmt(status.watch.expiration, true)}、{status.renew_schedule} に自動更新)
              </Typography>
            )}
          </Box>

          <Typography variant="caption" color="text.secondary">最終通知</Typography>
          <Typography sx={{ fontSize: '0.8rem' }}>
            {fmt(status?.watch.last_push_at, true)}
            {status && (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1.5 }}>
                今日の通知 {status.today.notified} 件 / ルール一致 {status.today.matched} 件
                {status.today.errors > 0 ? ` / エラー ${status.today.errors} 件` : ''}
              </Typography>
            )}
          </Typography>

          <Typography variant="caption" color="text.secondary">最終登録</Typography>
          <Typography sx={{ fontSize: '0.8rem' }}>{fmt(status?.watch.updated_at, true)}</Typography>
        </Box>
        {view.warn && (
          <Alert severity={view.label === '未登録' ? 'info' : 'warning'} sx={{ mt: 1, fontSize: '0.78rem', py: 0 }}>
            {view.warn}
          </Alert>
        )}
      </Box>

      {/* ルール一覧 */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: blueGrey[700], flexGrow: 1 }}>
          ルール ({status?.rules.length ?? 0} 件)
        </Typography>
        <Button
          size="small"
          startIcon={<Icons.Add />}
          onClick={() => setEditing({ ...NEW_RULE })}
          disabled={!status}
          sx={{ ...STANDARD_CTA_BUTTON_SX, fontSize: '0.75rem', py: 0.25 }}
        >
          ルールを追加
        </Button>
      </Box>
      {(status?.rules ?? []).length === 0 ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          ルールはまだありません。「ルールを追加」から条件と指示を登録してください。
        </Typography>
      ) : (
        (status?.rules ?? []).map((r) => (
          <Box
            key={r.id}
            sx={{ p: 1.25, mb: 1, borderRadius: 1, bgcolor: '#fff', border: 1, borderColor: 'divider', opacity: r.enabled ? 1 : 0.7 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 0.5 }}>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, flexGrow: 1 }}>{r.name}</Typography>
              <Chip
                label={MAIL_RULE_ACTION_LABELS[r.action] ?? r.action}
                size="small"
                variant="outlined"
                sx={{ height: 18, fontSize: '0.65rem' }}
              />
              <Tooltip title={r.enabled ? '無効にする (判定しなくなります)' : '有効にする'}>
                <span>
                  <Switch
                    size="small"
                    checked={r.enabled}
                    disabled={busyRuleId === r.id}
                    onChange={(e) => void handleToggle(r, e.target.checked)}
                  />
                </span>
              </Tooltip>
              <Tooltip title="編集 / テスト投入">
                <span>
                  <IconButton size="small" onClick={() => setEditing(toInput(r))} disabled={busyRuleId === r.id}>
                    <Icons.Edit sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="削除">
                <span>
                  <IconButton size="small" onClick={() => void handleDelete(r)} disabled={busyRuleId === r.id}>
                    <Icons.Delete sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            {busyRuleId === r.id && <LoadingView message="" height="20px" size={16} />}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '120px 1fr' }, rowGap: 0.25, columnGap: 1 }}>
              <Typography variant="caption" color="text.secondary">差出人</Typography>
              <Typography sx={{ fontSize: '0.78rem' }}>{r.from_email}</Typography>
              <Typography variant="caption" color="text.secondary">件名に含む</Typography>
              <Typography sx={{ fontSize: '0.78rem' }}>{r.subject_contains || '(条件なし)'}</Typography>
              <Typography variant="caption" color="text.secondary">保存先グループ</Typography>
              <Typography sx={{ fontSize: '0.78rem' }}>{r.group_name || '共通'}</Typography>
              <Typography variant="caption" color="text.secondary">会話のタイトル</Typography>
              <Typography sx={{ fontSize: '0.78rem' }}>{r.title_format}</Typography>
              <Typography variant="caption" color="text.secondary">ルール・スキル</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {r.refs?.defs.length ? (
                  r.refs.defs.map((x, i) => (
                    <Chip
                      key={x.id}
                      label={`${i + 1}. ${DEF_TYPE_LABEL[x.type] ?? x.type}: ${x.name}`}
                      size="small"
                      color={x.type === 'skill' ? 'primary' : 'secondary'}
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.68rem' }}
                    />
                  ))
                ) : (
                  <Typography sx={{ fontSize: '0.78rem' }}>(未選択)</Typography>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">取組み課題</Typography>
              <Typography sx={{ fontSize: '0.78rem' }}>
                {r.refs?.issues.length ? r.refs.issues.map((x) => x.name).join('、') : 'なし'}
              </Typography>
              {r.note && (
                <>
                  <Typography variant="caption" color="text.secondary">補足の指示</Typography>
                  <Typography sx={{ fontSize: '0.78rem', whiteSpace: 'pre-wrap' }}>{r.note}</Typography>
                </>
              )}
            </Box>
            {(r.problems ?? []).length > 0 && (
              <Alert severity="error" sx={{ mt: 0.75, fontSize: '0.76rem', py: 0 }}>
                このルールは止まっています: {(r.problems ?? []).join('、')}
              </Alert>
            )}
            {(r.warnings ?? []).length > 0 && (
              <Alert severity="warning" sx={{ mt: 0.75, fontSize: '0.76rem', py: 0 }}>
                {(r.warnings ?? []).join('、')}
              </Alert>
            )}
          </Box>
        ))
      )}

      {/* 処理履歴 */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, mb: 0.5 }}>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: blueGrey[700], flexGrow: 1 }}>
          処理履歴 (直近 {status?.records.length ?? 0} 件)
        </Typography>
        <FormControlLabel
          control={<Switch size="small" checked={showSkipped} onChange={(e) => setShowSkipped(e.target.checked)} />}
          label={<Typography variant="caption">対象外も表示 ({skippedCount})</Typography>}
          sx={{ mr: 0 }}
        />
      </Box>
      {loading && status && <LoadingView message="更新中..." height="40px" size={20} />}
      {records.length === 0 ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {showSkipped ? '処理記録はまだありません。' : 'ルールに一致した処理はまだありません。'}
        </Typography>
      ) : (
        <Table size="small" sx={{ bgcolor: '#fff', '& td, & th': { fontSize: '0.76rem', py: 0.5 } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>処理日時</TableCell>
              <TableCell>差出人</TableCell>
              <TableCell>件名</TableCell>
              <TableCell>判定</TableCell>
              <TableCell>ルール</TableCell>
              <TableCell>会話</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map((r: MailAutomationRecord) => {
              const st = RECORD_STATUS[r.status] ?? { label: r.status || '-', color: 'default' as const };
              return (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmt(r.created_at)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{senderName(r.from) || '-'}</TableCell>
                  <TableCell sx={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Tooltip title={r.subject || ''}>
                      <span>{r.subject || '-'}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title={r.error || r.reason || ''}>
                      <Chip label={st.label} size="small" color={st.color} variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                    </Tooltip>
                    {r.manual && (
                      <Chip label="手動" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem', ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {r.rule_id ? ruleNameById.get(r.rule_id) ?? (r.rule_id === 'draft' ? '(未保存の下書き)' : r.rule_id) : '-'}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {r.session_id ? (
                      <Link component="button" underline="hover" sx={{ fontSize: '0.76rem' }} onClick={() => onOpenSession(r.session_id)}>
                        開く
                      </Link>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {editing && (
        <RuleEditDialog
          open={Boolean(editing)}
          initial={editing}
          groups={groups}
          defs={personalDefs}
          issues={visibleIssues}
          groupName={groupName}
          me={currentUserEmail}
          onClose={() => setEditing(null)}
          onSave={handleSave}
          testCandidates={testCandidates}
          testRun={testRun}
          onTested={() => void refresh()}
        />
      )}
    </Box>
  );
}
